import type { RefObject } from 'react'
import { useState } from 'react'
import { ReactComponent as ChecklistIcon } from 'assets/icons/checklist.svg'
import { SaveButton } from 'components/ActionButton'
import DURCard from 'components/DURCard'
import IconWrapper from 'components/IconWrapper'
import { ModalWrapper, ModalHeader } from 'components/Modal'
import { CancelButton } from 'components/PageStructure'
import useAutoFocus from 'hooks/useAutoFocus'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import styled from 'styled-components'
import { bodyPrimaryColor, subduedColor } from 'styles/styleVariables'
import type { Fill, DUR } from 'types'
import { sortDURsBySeverity } from 'utils'

const randomInt = (max: number): number => {
  return Math.floor(Math.random() * Math.floor(max))
}

const generateTestValue = (): string => {
  let code = ''
  for (let i = 0; i < 4; i++) {
    code += `${randomInt(9)}`
  }
  return code
}

type DURConfirmationModalProps = { fill: Fill; confirmationCallback: () => void }

const DURConfirmationModal = ({ fill, confirmationCallback }: DURConfirmationModalProps): JSX.Element => {
  const { dismissModal } = useModalContext()

  const [testValue] = useState(generateTestValue())
  const [inputValue, setInputValue] = useState('')

  const testPassed = inputValue === testValue
  const autoFocusRef = useAutoFocus()

  return (
    <ModalWrapper id="DURConfirmationModal">
      <ModalHeader>
        <IconWrapper>
          <ChecklistIcon fill={bodyPrimaryColor} />
        </IconWrapper>
        <h2>Clinical review confirmation</h2>
      </ModalHeader>
      <ClinicalReviewCardList fill={fill} />
      <ConfirmationText>
        By entering the 4 digit pin, I confirm I have performed a clinical review for this medication.
      </ConfirmationText>
      <form
        onSubmit={e => {
          e.preventDefault()
          if (testPassed) {
            confirmationCallback()
            dismissModal()
          }
        }}
      >
        <InputContainer>
          <StyledInput
            data-testid="dur-pin-input"
            ref={autoFocusRef as RefObject<HTMLInputElement>}
            onChange={e => setInputValue(e.target.value)}
          />
          <TestValue data-testid="dur-pin-display">{testValue}</TestValue>
        </InputContainer>
      </form>
      <ButtonsContainer>
        <CancelButton label={'Cancel'} onClick={dismissModal} />
        <SaveButton
          disabled={!testPassed}
          isModal
          label={'Submit'}
          onClick={() => testPassed && confirmationCallback()}
        />
      </ButtonsContainer>
    </ModalWrapper>
  )
}

const ClinicalReviewCardList = (props: { fill: Fill }) => {
  const screenings = props.fill.durScreenings
  const durs = (screenings && screenings.length && screenings[screenings.length - 1].results) || []

  const sortedDurs: DUR[] = sortDURsBySeverity(durs)

  return (
    <StyledClinicalReviewCardList>
      {sortedDurs.map(dur => (
        <DURCard key={`${dur.status} - ${dur.type}`} {...dur} />
      ))}
    </StyledClinicalReviewCardList>
  )
}

const InputContainer = styled.ul`
  border: 0.125rem solid ${subduedColor};
  border-radius: 0.25rem;
  background-color: ${subduedColor};
  margin-top: 0.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: min-content;
`
const StyledInput = styled.input`
  text-align: center;
  font-family: 'roboto';
  font-size: 1rem;
  font-weight: 500;
  height: 2.5rem;
  width: 3.6rem;
  padding: 0rem;
`

const TestValue = styled.li`
  line-height: 1.2rem;
  height: 2.5rem;
  font-weight: 500;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 3.6rem;
`

const StyledClinicalReviewCardList = styled.ul`
  > li {
    margin-top: 0.625rem;
    margin-bottom: 0.625rem;
  }
`

const ConfirmationText = styled.p`
  margin-top: 1.25rem;
  font-size: 1rem;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
`

export default DURConfirmationModal
